const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000"

export class ApiError extends Error {}

/** A request that never got an answer: the server is down, restarting, or blocked by the browser. */
const UNREACHABLE =
  "Could not reach the server. It may be restarting, so try again in a moment."

async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (error) {
    // Leave deliberate cancellations alone: they are not a failure to show.
    if (error instanceof DOMException && error.name === "AbortError")
      throw error

    throw new ApiError(UNREACHABLE)
  }
}

function errorMessage(body: unknown, status: number): string {
  if (!body || typeof body !== "object" || !("message" in body)) {
    return `HTTP ${status}`
  }

  const { message } = body as { message: unknown }

  if (typeof message === "string") {
    return message
  }

  if (Array.isArray(message)) {
    return message
      .map((issue: unknown) =>
        issue && typeof issue === "object" && "message" in issue
          ? String(issue.message)
          : String(issue)
      )
      .join("; ")
  }

  return `HTTP ${status}`
}

/** Fetches a binary response (such as a PDF) with the session cookie. */
export async function apiBlob(path: string): Promise<Blob> {
  const response = await send(`${API_URL}/api${path}`, {
    credentials: "include",
  })

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null)
    throw new ApiError(errorMessage(body, response.status))
  }

  return response.blob()
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const response = await send(`${API_URL}/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  })

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null)
    throw new ApiError(errorMessage(body, response.status))
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}
